/* ─────────────────────────────────────────────────────────────
   AUTH CONTEXT — Single source of truth for the logged-in user.

   Provides:
   - user:    the Supabase user object, or null if logged out
   - loading: true while we're checking the session on first load
   - signOut: call this to log the user out

   Wrap your app in <AuthProvider> (done in App.js).
   Read auth state in any component with: const { user } = useAuth()
   ───────────────────────────────────────────────────────────── */

import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../config/supabaseClient';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if there's already an active session when the app loads
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });

    // Listen for any auth changes: login, logout, token refresh, etc.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
      }
    );

    // Clean up the listener when the component unmounts
    return () => subscription.unsubscribe();
  }, []);

  async function signOut() {
    await supabase.auth.signOut();
  }

  return (
    <AuthContext.Provider value={{ user, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook — use this in any component to access auth state
export function useAuth() {
  return useContext(AuthContext);
}
